/**
 * useTimeframeAlignment — polling wrapper that classifies a symbol's
 * stance on each of N timeframes for the Live Coach Feed alignment pills.
 *
 * One hook call per LiveSymbolPanel. Internally fans out a fetchBars
 * request per timeframe (using the shared barDataService cache, so two
 * panels watching MNQ + MES with overlapping TFs don't pay extra I/O).
 *
 * Polls every 60s — same cadence as useLivePatternStats / useCounterBiasWhisper,
 * which lets all three live surfaces refresh together off the same bar
 * cache and prevents the alignment pills from looking stale next to fresh
 * pattern fires.
 */

import { useEffect, useMemo, useState } from 'react';
import { barDataService } from '../services/barData';
import { classifyTimeframeStance, STANCE } from '../services/barAnalysis/timeframeAlignment';

const POLL_INTERVAL_MS = 60 * 1000;

/**
 * @param {Object} params
 * @param {string}   params.symbol              Ticker root.
 * @param {string[]} params.timeframes          Array of TF strings ('5m', '15m', '1h', '4h', ...)
 * @param {string}   params.currentTradingDay   YYYY-MM-DD — fetches bars for this day.
 * @param {boolean}  [params.enabled=true]      Master switch.
 *
 * @returns {Object}
 *   {
 *     alignment,   // Array<{ timeframe, status }> — status one of STANCE.*
 *     isPolling,   // bool
 *   }
 */
export const useTimeframeAlignment = ({
  symbol,
  timeframes,
  currentTradingDay,
  enabled = true,
} = {}) => {
  const [alignment, setAlignment] = useState([]);
  const [isPolling, setIsPolling] = useState(false);

  // Stable string key so the effect doesn't fire when caller passes a new
  // array reference with the same contents.
  const tfKey = useMemo(
    () => (Array.isArray(timeframes) ? timeframes.join(',') : ''),
    [timeframes]
  );

  const shouldPoll = Boolean(
    enabled &&
    symbol &&
    currentTradingDay &&
    tfKey
  );

  // Reset on context change so a symbol switch doesn't show stale stance
  // from the previous symbol while the next poll is in flight.
  useEffect(() => {
    setAlignment([]);
  }, [symbol, tfKey, currentTradingDay]);

  useEffect(() => {
    if (!shouldPoll) {
      setIsPolling(false);
      return undefined;
    }
    setIsPolling(true);

    let cancelled = false;

    const pollOnce = async () => {
      const tfs = tfKey.split(',').filter(Boolean);
      // Concurrent fetches — bar cache makes repeated reads of the same
      // CSV essentially free, so this is fast even for 3 timeframes.
      const results = await Promise.all(tfs.map(async tf => {
        try {
          const res = await barDataService.fetchBars(
            { symbol, dateStr: currentTradingDay, timeframe: tf },
            { skipCache: false }
          );
          const bars = Array.isArray(res?.bars) ? res.bars : [];
          const status = classifyTimeframeStance(bars);
          return { timeframe: tf, status };
        } catch {
          return { timeframe: tf, status: STANCE.UNKNOWN };
        }
      }));
      if (!cancelled) setAlignment(results);
    };

    pollOnce();
    const id = setInterval(pollOnce, POLL_INTERVAL_MS);
    return () => {
      cancelled = true;
      clearInterval(id);
    };
  }, [shouldPoll, symbol, tfKey, currentTradingDay]);

  return { alignment, isPolling };
};

export default useTimeframeAlignment;
